import axios from 'axios';
import { API_DOMAIN } from '../../../components/constants';

// The backend routes exist, but the API reference says the web must not offer
// "Forgot password?" until the migration, configuration and a live email test
// are done. Flip this once a reset email has been received end to end.
export const PASSWORD_RESET_ENABLED = false;

export const MIN_PASSWORD_LENGTH = 8;
export const REQUEST_MESSAGE = 'If an account exists for this email, a reset link has been sent.';

export class PasswordResetError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

// Public routes: no access token is sent.
const post = (path, body) => axios.post(`${API_DOMAIN}/auth/password-reset/${path}`, body);

function toResetError(error) {
  const body = error?.response?.data;
  const code = body?.code || body?.data?.code || (error?.response ? 'PASSWORD_RESET_UNAVAILABLE' : 'NETWORK');
  if (code === 'PASSWORD_RESET_INVALID') return new PasswordResetError('This reset link is invalid or has expired.', code);
  if (code === 'PASSWORD_RESET_PASSWORD_INVALID') return new PasswordResetError(body?.message || 'Choose a different password.', code);
  if (code === 'NETWORK') return new PasswordResetError('Could not reach the server. Please try again.', code);
  return new PasswordResetError('Password reset is temporarily unavailable. Please try again later.', code);
}

export async function requestPasswordReset(email) {
  try {
    const res = await post('request', { email: String(email || '').trim() });
    return res.data?.message || REQUEST_MESSAGE;
  } catch (error) { throw toResetError(error); }
}

export async function checkResetToken(token) {
  try {
    const res = await post('check', { token });
    return res.data?.data?.valid === true;
  } catch (error) { throw toResetError(error); }
}

export async function confirmPasswordReset(token, newPassword) {
  try {
    const res = await post('confirm', { token, new_password: newPassword });
    if (res.data?.success === false) throw Object.assign(new Error(), { response: { data: res.data } });
    return res.data?.message || 'Password changed. Please sign in again.';
  } catch (error) { throw toResetError(error); }
}

// Read the token once and drop it from the address bar and history, so it is
// not kept in browser history or leaked through a Referer header.
export function takeTokenFromLocation(location = window.location, history = window.history) {
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';
  if (params.has('token')) {
    params.delete('token');
    const rest = params.toString();
    history.replaceState(history.state, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
  }
  return token;
}

export function validateNewPassword(password, confirm) {
  if (!password || !password.trim()) return 'Enter a new password.';
  if (password.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (password !== confirm) return 'The passwords do not match.';
  return null;
}
